import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, Grid } from "./common";
import { Plate, Meter, StatusBadge, Avatar, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { useStore, STUDIO, actions, money, outstanding, currentStage, stageProgress } from "../lib/store";
import { STAGE_ORDER } from "../lib/store";
import { useCan, useIsAdmin } from "../lib/auth";
import { BUILDING_TYPES } from "../lib/program";
import { useSession, logout } from "../lib/auth";
import { TeamPanel } from "../components/TeamPanel";
import { useTheme } from "../components/Shell";
import { SyncBadge } from "../components/SyncBadge";

const BLANK = { name: "", type: "Private Residence", location: "", area: "", budget: "", currency: "USD", clientName: "", clientCompany: "", clientEmail: "", surveyPlan: "unknown", lat: "", lng: "", existing: false, currentStage: "concept", templateId: "" };

export default function Projects() {
  const { projects, templates = [] } = useStore();
  const nav = useNavigate();
  const canEdit = useCan("projects");
  const isAdmin = useIsAdmin();
  const session = useSession();
  // the accountant opens a project on its money, not its design
  const landing = session?.teamRole === "accountant" ? "ledger" : "dashboard";
  const [theme, toggleTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [f, setF] = useState({ ...BLANK });
  const doomed = projects.find((p) => p.id === confirmDel);
  const totalOwed = projects.reduce((s, p) => s + outstanding(p), 0);

  function create() {
    if (!f.name.trim() || !f.clientName.trim()) return;
    const id = actions.createProject({
      name: f.name.trim(), type: f.type, location: f.location.trim(), area: f.area.trim(),
      budget: Number(f.budget) || 0, currency: f.currency,
      clientName: f.clientName.trim(), clientCompany: f.clientCompany.trim(), clientEmail: f.clientEmail.trim(),
      surveyPlan: f.surveyPlan as any,
      geo: f.lat && f.lng ? { lat: Number(f.lat), lng: Number(f.lng) } : undefined,
      existing: f.existing, currentStage: f.existing ? (f.currentStage as any) : undefined,
      templateId: f.templateId || undefined,
    });
    setF({ ...BLANK }); setOpen(false);
    nav(`/studio/${id}/${landing}`);
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      {/* full-width top bar (no sidebar here) */}
      <div className="row between" style={{ padding: "16px clamp(20px,5vw,48px)", borderBottom: "1px solid var(--line)", position: "sticky", top: 0, zIndex: 20, background: "color-mix(in srgb, var(--paper) 82%, transparent)", backdropFilter: "blur(12px)" }}>
        <div className="row" style={{ gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={20} /></div>
          <div><div className="serif" style={{ fontSize: 18, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Studio Cockpit</div></div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <SyncBadge />
          <button className="iconbtn" onClick={toggleTheme} style={{ width: 36, height: 36 }} aria-label="Toggle theme">{theme === "light" ? <Icon.moon width={16} /> : <Icon.sun width={16} />}</button>
          <div className="row" style={{ gap: 9, padding: "4px 6px 4px 4px" }}><Avatar name={session?.name ?? STUDIO.principal} hue={20} size={30} /><span style={{ fontSize: 13, fontWeight: 500 }}>{session?.name ?? STUDIO.principal}</span></div>
          <button className="iconbtn" onClick={() => { logout(); nav("/login"); }} style={{ width: 36, height: 36 }} title="Sign out"><Icon.back width={16} /></button>
        </div>
      </div>

      <Page>
        <div className="row between wrap reveal" style={{ marginBottom: 30, gap: 16 }}>
          <div>
            <div className="kicker">{STUDIO.name} — Portfolio</div>
            <h1 className="serif" style={{ fontSize: 46, marginTop: 8 }}>Projects at a glance</h1>
            <p className="muted" style={{ marginTop: 8 }}>{projects.length} live engagements · {money(totalOwed)} outstanding · select a project to open its cockpit</p>
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <button className="btn ghost" onClick={() => nav("/studio/pipeline")}><Icon.money width={15} />Pipeline</button>
            <button className="btn ghost" onClick={() => nav("/studio/tools")}><Icon.spark width={15} />Studio tools</button>
            {isAdmin && <button className="btn ghost" onClick={() => setTeamOpen(true)}><Icon.layers width={15} />Team &amp; access</button>}
            {canEdit && <button className="btn accent" onClick={() => setOpen(true)}><Icon.plus width={16} />New project</button>}
          </div>
        </div>

        <Grid cols="repeat(auto-fill, minmax(340px, 1fr))" gap={22}>
          {canEdit && (
            <button className="reveal" onClick={() => setOpen(true)} style={{ minHeight: 320, borderRadius: "var(--r-lg)", border: "1.5px dashed var(--line-2)", background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", color: "var(--ink-3)", transition: "border-color .25s, color .25s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent-ink)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-2)"; e.currentTarget.style.color = "var(--ink-3)"; }}>
              <div style={{ textAlign: "center" }}><Icon.plus width={30} /><div style={{ marginTop: 10, fontWeight: 500 }}>New project</div><div className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>Start a new engagement</div></div>
            </button>
          )}
          {projects.map((p) => {
            const stage = currentStage(p);
            const owed = outstanding(p);
            const activeIdx = STAGE_ORDER.findIndex((s) => s.key === stage.key);
            return (
              <div key={p.id} style={{ position: "relative" }}>
              <button className="card hoverable reveal" onClick={() => nav(`/studio/${p.id}/${landing}`)} style={{ textAlign: "left", padding: 0, overflow: "hidden", cursor: "pointer", border: "1px solid var(--line)", background: "var(--card)", width: "100%", display: "block" }}>
                <div style={{ position: "relative" }}>
                  <Plate hue={p.cover} h={148} seed={2} />
                  <div style={{ position: "absolute", top: 12, right: 12 }}><span className="badge accent" style={{ background: "rgba(255,255,255,.9)" }}><span className="dot" />{stage.label}</span></div>
                </div>
                <div style={{ padding: 20 }}>
                  <div className="row between">
                    <div className="kicker">{p.code}</div>
                    {owed > 0 && <span className="badge warn"><span className="dot" />{money(owed, p.currency)} due</span>}
                  </div>
                  <h3 className="serif" style={{ fontSize: 24, marginTop: 8 }}>{p.name}</h3>
                  <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{p.type} · {p.location || "—"}</div>

                  {/* stage-at-a-glance strip */}
                  <div className="row" style={{ gap: 4, marginTop: 16 }}>
                    {STAGE_ORDER.map((s, i) => (
                      <div key={s.key} title={s.label} style={{ flex: 1, height: 5, borderRadius: 5, background: i < activeIdx ? "var(--accent)" : i === activeIdx ? "var(--ink)" : "var(--line-2)" }} />
                    ))}
                  </div>
                  <div className="row between" style={{ fontSize: 12, marginTop: 8 }}>
                    <span className="dim">Stage {activeIdx + 1} of {STAGE_ORDER.length} · <b style={{ color: "var(--ink)" }}>{stage.label}</b></span>
                    <span className="mono">{stageProgress(p)}%</span>
                  </div>

                  <div className="divider" style={{ margin: "16px 0 14px" }} />
                  <div className="row between">
                    <div className="row" style={{ gap: 9 }}><Avatar name={p.client.name} hue={p.client.avatarHue} size={28} /><span style={{ fontSize: 13 }}>{p.client.name}</span></div>
                    <span className="row dim" style={{ gap: 4, fontSize: 12 }}>Open <Icon.arrow width={14} /></span>
                  </div>
                </div>
              </button>
              {isAdmin && (
                <button className="iconbtn" title={`Remove ${p.name}`} onClick={() => setConfirmDel(p.id)}
                  style={{ position: "absolute", top: 12, left: 12, width: 30, height: 30, background: "rgba(255,255,255,.92)", border: "1px solid var(--line)", color: "var(--ink-2)" }}>
                  <Icon.close width={13} />
                </button>
              )}
              </div>
            );
          })}
        </Grid>
      </Page>

      <Modal open={teamOpen} onClose={() => setTeamOpen(false)} title="Team & access" wide>
        <p className="muted" style={{ fontSize: 14, marginBottom: 18 }}>
          Assign roles and set what each one can reach — without opening a project.
        </p>
        <TeamPanel compact />
      </Modal>

      <Modal open={!!doomed} onClose={() => setConfirmDel(null)} title="Remove project">
        {doomed && (
          <div className="col" style={{ gap: 14 }}>
            <p className="muted" style={{ fontSize: 14.5, lineHeight: 1.6 }}>
              Remove <b>{doomed.name}</b> <span className="mono dim">({doomed.code})</span> and everything in it — brief, register, quotations, reports and the client's portal access.
            </p>
            <div className="panel" style={{ padding: "10px 12px", fontSize: 12.5, borderColor: "var(--warn)" }}>
              This can't be undone. {outstanding(doomed) > 0 && <b>{money(outstanding(doomed), doomed.currency)} is still outstanding on it.</b>}
            </div>
            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn" style={{ background: "var(--danger)", color: "#fff", borderColor: "var(--danger)" }}
                onClick={() => { actions.deleteProject(doomed.id); setConfirmDel(null); }}>
                <Icon.close width={14} />Remove permanently
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New project" wide>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* start from a saved skeleton — register, fee stages, brief headings */}
          {templates.length > 0 && (
            <div className="field" style={{ gridColumn: "1 / -1" }}><label>Start from a template <span className="dim">— carries the register, fee stages and brief headings</span></label>
              <select className="select" value={f.templateId} onChange={(e) => setF({ ...f, templateId: e.target.value })}>
                <option value="">Standard package for the building type</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.deliverables.length} sheets</option>)}
              </select>
            </div>
          )}
          {/* new engagement vs onboarding a project already under way */}
          <div className="field" style={{ gridColumn: "1 / -1" }}><label>Project status</label>
            <div className="row" style={{ gap: 8 }}>
              {[["new", "New engagement"], ["existing", "Existing — already signed"]].map(([v, l]) => {
                const on = f.existing === (v === "existing");
                return <button key={v} type="button" onClick={() => setF({ ...f, existing: v === "existing" })} className="chip" style={{ flex: 1, background: on ? "var(--ink)" : "var(--card)", color: on ? "var(--paper)" : "var(--ink)", borderColor: on ? "var(--ink)" : "var(--line-2)" }}>{l}</button>;
              })}
            </div>
          </div>
          {f.existing && (
            <div className="field" style={{ gridColumn: "1 / -1" }}><label>Current stage <span className="dim">— the engagement is set signed &amp; the brief approved</span></label>
              <select className="select" value={f.currentStage} onChange={(e) => setF({ ...f, currentStage: e.target.value })}>
                {STAGE_ORDER.filter((s) => ["concept", "developed", "presentation", "documentation", "delivery"].includes(s.key)).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}
          <div className="field" style={{ gridColumn: "1 / -1" }}><label>Project name</label><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Harbour House" /></div>
          <div className="field"><label>Building type</label>
            <select className="select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              {BUILDING_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Location</label><input className="input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="City, Country" /></div>
          <div className="field"><label>Area</label><input className="input" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} placeholder="e.g. 320 m²" /></div>
          <div className="field"><label>Construction budget <span className="dim">— preliminary</span></label>
            <div className="row" style={{ gap: 8 }}>
              <select className="select" style={{ width: 92 }} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>{["USD", "EUR", "GBP", "GYD"].map((c) => <option key={c}>{c}</option>)}</select>
              <input className="input" type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} placeholder="285000" />
            </div>
            <span className="dim" style={{ fontSize: 11.5, marginTop: 4, display: "block" }}>The estimated cost of the works — prices the design fee and travels to Bid / Project Manager.</span>
          </div>
          {/* site & survey */}
          <div className="field" style={{ gridColumn: "1 / -1" }}><div className="divider" style={{ margin: "4px 0" }} /><label style={{ marginTop: 8 }}>Survey plan</label>
            <div className="row" style={{ gap: 8 }}>
              {[["yes", "Available"], ["no", "Not available"], ["unknown", "To confirm"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF({ ...f, surveyPlan: v })} className="chip" style={{ flex: 1, background: f.surveyPlan === v ? "var(--ink)" : "var(--card)", color: f.surveyPlan === v ? "var(--paper)" : "var(--ink)", borderColor: f.surveyPlan === v ? "var(--ink)" : "var(--line-2)" }}>{l}</button>
              ))}
            </div>
          </div>
          <div className="field"><label>Latitude <span className="dim">(pin)</span></label><input className="input" value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} placeholder="6.8013" /></div>
          <div className="field"><label>Longitude</label>
            <div className="row" style={{ gap: 8 }}>
              <input className="input" value={f.lng} onChange={(e) => setF({ ...f, lng: e.target.value })} placeholder="-58.1551" />
              <button type="button" className="btn soft sm" title="Use my location" onClick={() => navigator.geolocation?.getCurrentPosition((pos) => setF((c) => ({ ...c, lat: pos.coords.latitude.toFixed(4), lng: pos.coords.longitude.toFixed(4) })))}><Icon.pin width={15} /></button>
            </div>
          </div>
          {f.lat && f.lng && (
            <div style={{ gridColumn: "1 / -1", borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)" }}>
              <iframe title="Pin" width="100%" height="160" style={{ border: 0, display: "block" }} loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(f.lng) - 0.02}%2C${Number(f.lat) - 0.012}%2C${Number(f.lng) + 0.02}%2C${Number(f.lat) + 0.012}&layer=mapnik&marker=${f.lat}%2C${f.lng}`} />
            </div>
          )}
          <div className="field" style={{ gridColumn: "1 / -1" }}><div className="divider" style={{ margin: "4px 0" }} /><label style={{ marginTop: 8 }}>Client contact</label><input className="input" value={f.clientName} onChange={(e) => setF({ ...f, clientName: e.target.value })} placeholder="Contact name" /></div>
          <div className="field"><label>Company</label><input className="input" value={f.clientCompany} onChange={(e) => setF({ ...f, clientCompany: e.target.value })} placeholder="Company / family office" /></div>
          <div className="field"><label>Client email</label><input className="input" value={f.clientEmail} onChange={(e) => setF({ ...f, clientEmail: e.target.value })} placeholder="name@email.com" /></div>
        </div>
        <div className="row between" style={{ marginTop: 20 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>A space program & client portal code are generated automatically.</span>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn accent" onClick={create} disabled={!f.name.trim() || !f.clientName.trim()}><Icon.check width={15} />Create project</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
